import { colorSystem } from '@luminovo/design-system';
import { formatDecimal, getLocale } from '@luminovo/frontend-utils';
import { styled } from '@mui/material';
import React from 'react';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { chartTheme } from '../chartTheme';
import { ChartTooltip } from '../StackedBarChart';
import { dedupeTicks } from './dedupeTicks';
import { type TimeSeriesDatum } from './types';

const ChartWrapper = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minHeight: 0,
    fontVariantNumeric: 'tabular-nums',
    // Recharts' accessibility layer makes the svg a tab stop whose arrow keys step through the points.
    '& .recharts-surface:focus-visible': {
        outline: `2px solid ${colorSystem.primary[5]}`,
        outlineOffset: '2px',
        borderRadius: 4,
    },
});

const LINE_COLOR = colorSystem.primary[6];

/** The single series the chart plots, named so the shared tooltip can key on it. */
const SERIES_KEY = 'value';

export type TimeSeriesChartProps = {
    data: TimeSeriesDatum[];
    /** Name of the plotted series, shown in the tooltip. */
    seriesLabel: string;
    /** Formats the x-axis ticks. Defaults to a short month and year in the active locale. */
    formatTime?: (time: number) => string;
    /** Formats the tooltip heading. Defaults to `formatTime`. */
    formatTooltipTime?: (time: number) => string;
    /** Formats the y-axis ticks and the tooltip value. */
    formatValue?: (value: number) => string;
    /**
     * Starts the y axis at zero and fills the area under the line. Turn it off for values that only
     * move within a narrow band far from zero, like an index around 100: the axis then fits the data
     * and the chart draws the line alone, since a fill would imply a magnitude the axis doesn't show.
     */
    includeZero?: boolean;
    animationDuration?: number;
};

function formatShortMonth(time: number): string {
    return new Date(time).toLocaleDateString(getLocale(), { month: 'short', year: 'numeric' });
}

/**
 * A single-series area chart over time. The counterpart of `StackedBarChart` for
 * data that is bucketed by date rather than by category.
 */
export function TimeSeriesChart({
    data,
    seriesLabel,
    formatTime = formatShortMonth,
    formatTooltipTime,
    formatValue = (value) => formatDecimal(value),
    includeZero = true,
    animationDuration = 300,
}: TimeSeriesChartProps) {
    // Recharts renders `defs` into the page, so the gradient needs an id that is unique per instance.
    const gradientId = `time-series-gradient-${React.useId().replace(/:/g, '')}`;

    const sortedData = React.useMemo(() => [...data].sort((a, b) => a.time - b.time), [data]);
    const ticks = React.useMemo(
        () =>
            dedupeTicks(
                sortedData.map((datum) => datum.time),
                formatTime,
            ),
        [sortedData, formatTime],
    );

    const formatHeading = formatTooltipTime ?? formatTime;
    const seriesMeta = React.useMemo(
        () => ({ [SERIES_KEY]: { label: seriesLabel, color: LINE_COLOR } }),
        [seriesLabel],
    );

    return (
        <ChartWrapper>
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={sortedData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
                    <defs>
                        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={colorSystem.primary[5]} stopOpacity={0.16} />
                            <stop offset="100%" stopColor={colorSystem.primary[5]} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid vertical={false} stroke={colorSystem.neutral[2]} />
                    <XAxis
                        dataKey="time"
                        type="number"
                        scale="time"
                        domain={['dataMin', 'dataMax']}
                        ticks={ticks}
                        tick={chartTheme.axisTick}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value: number) => formatTime(value)}
                        minTickGap={24}
                        // The first tick sits at the left edge, under the y labels: keep the rows apart.
                        tickMargin={8}
                    />
                    <YAxis
                        dataKey={SERIES_KEY}
                        type="number"
                        allowDecimals={false}
                        domain={includeZero ? [0, 'auto'] : ['auto', 'auto']}
                        width="auto"
                        // The default 'preserveEnd' drops the second-highest tick on a chart as short as 140 px.
                        interval={0}
                        tick={chartTheme.axisTick}
                        tickLine={false}
                        axisLine={false}
                        tickFormatter={(value: number) => formatValue(value)}
                    />
                    <Tooltip
                        cursor={{ stroke: colorSystem.neutral[4], strokeWidth: 1 }}
                        content={(props) => {
                            const payload = (props.payload ?? []) as ReadonlyArray<{ payload: TimeSeriesDatum }>;
                            const datum = payload[0]?.payload;
                            // The tooltip lists no zero value, so a zero point would get an empty frame.
                            if (!datum || datum.value === 0) {
                                return null;
                            }
                            const heading = formatHeading(datum.time);
                            return (
                                <div
                                    style={{
                                        background: colorSystem.neutral.white,
                                        ...chartTheme.tooltip,
                                        padding: '4px 10px',
                                    }}
                                >
                                    <ChartTooltip<typeof SERIES_KEY>
                                        active={props.active}
                                        label={heading}
                                        // ChartTooltip reads `dataKey` and `value`; the nested datum only
                                        // exists to satisfy the payload shape the bar charts share.
                                        payload={[
                                            {
                                                dataKey: SERIES_KEY,
                                                value: datum.value,
                                                payload: { label: heading, sourceRows: [], value: datum.value },
                                            },
                                        ]}
                                        seriesMeta={seriesMeta}
                                        formatValue={formatValue}
                                        visibleKeys={[SERIES_KEY]}
                                    />
                                </div>
                            );
                        }}
                    />
                    <Area
                        type="linear"
                        dataKey={SERIES_KEY}
                        stroke={LINE_COLOR}
                        strokeWidth={2}
                        fill={includeZero ? `url(#${gradientId})` : 'none'}
                        dot={false}
                        activeDot={{ r: 4, fill: LINE_COLOR, stroke: colorSystem.neutral.white, strokeWidth: 2 }}
                        animationDuration={animationDuration}
                    />
                </AreaChart>
            </ResponsiveContainer>
        </ChartWrapper>
    );
}
